import {
  CreateTimeOffRequestPayload,
  TimeOffDetail,
  TimeOffAllocationDetail,
} from '../store/slices/Requestor/createNewRequestSlice'
import { TimeOffRequestType } from './timeOffTypes'
import { HoursValues } from './balanceUtils'
import { convertTo24HourFormat } from './date'

/**
 * Map balance type codes to API type codes using TimeOffRequestType enum keys
 * Ensures type safety: only valid enum keys (V, F, D, O) can be used
 */
const TYPE_CODE_MAPPING: Record<string, keyof typeof TimeOffRequestType> = {
  vacation: 'V',
  float: 'F',
  flexible: 'D', // Flexible Personal Day = D
  other: 'O', // Other Leave = O
}

/**
 * Convert an already-24-hour time to HH:MM:SS format.
 * Used for the submission timestamp captured from the local clock.
 */
const formatTimeToHHMMSS = (hour?: string, minute?: string): string => {
  const h = hour ? String(hour).padStart(2, '0') : '00'
  const m = minute ? String(minute).padStart(2, '0') : '00'
  return `${h}:${m}:00`
}

/**
 * Convert a 12-hour partial-day time (hour + minute + AM/PM) into a 24-hour
 * `HH:MM:SS` string for the API payload. Falls back to AM when the meridiem is
 * missing so a PM selection is never silently shifted into the morning.
 *
 * @param hour - 12-hour clock hour ("1"-"12").
 * @param minute - Minute ("00"-"59").
 * @param ampm - Meridiem, "AM" or "PM" (case-insensitive).
 * @returns Time formatted as `HH:MM:SS` on a 24-hour clock.
 */
const formatPartialTimeToHHMMSS = (
  hour?: string,
  minute?: string,
  ampm?: string
): string => {
  const time24 = convertTo24HourFormat(
    hour || '1',
    minute || '00',
    (ampm || 'AM').toUpperCase()
  )
  return `${time24}:00`
}

/**
 * Format date and time to YYYY-MM-DD HH:MM:SS format (24-hour format)
 * If time is not provided, uses current time
 */
export const formatDateTime = (
  date: Date,
  time?: { hour: string; minute: string }
): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  // Use provided time or current time in 24-hour format
  let timeStr: string
  if (time) {
    timeStr = formatTimeToHHMMSS(time.hour, time.minute)
  } else {
    const now = new Date()
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const seconds = String(now.getSeconds()).padStart(2, '0')
    timeStr = `${hours}:${minutes}:${seconds}`
  }

  return `${year}-${month}-${day} ${timeStr}`
}

/**
 * Parse hours values into TimeOffAllocationDetail array
 * Only includes non-empty entries
 * When 'other' type has hours, uses the provided otherSubType
 * For birthday requests, returns special birthday entry with type 'B'
 * 
 * @param hoursValues - Hours for each category (vacation, float, flexible, other)
 * @param otherSubType - SubType code for 'other' category (e.g., 'OTH_ET', 'OTH_JD')
 * @param isBirthday - Whether this is a birthday request
 */
const buildTimeOffAllocationDetails = (
  hoursValues: HoursValues,
  otherSubType?: string,
  isBirthday?: boolean
): TimeOffAllocationDetail[] => {
  const details: TimeOffAllocationDetail[] = []

  // For birthday requests, add special birthday type entry
  if (isBirthday) {
    details.push({
      hours: '0',
      toTypeCd: 'B', // Birthday type
      toSubTypeCd: null,
    })
    return details // Return only birthday entry
  }

  Object.entries(hoursValues).forEach(([type, hours]) => {
    if (hours && Number(hours) > 0) {
      // For 'other' type, use the provided otherSubType, otherwise null
      const subTypeValue =
        type === 'other' && otherSubType ? otherSubType : null
      details.push({
        hours,
        toTypeCd: TYPE_CODE_MAPPING[type] || type,
        toSubTypeCd: subTypeValue,
      })
    }
  })

  return details
}

/**
 * Parameters for building time off details
 */
interface BuildTimeOffDetailsParams {
  selectedDates: string[]
  hoursValues: HoursValues
  duration: 'Full Day' | 'Partial Day'
  startTime?: { hour: string; minute: string; ampm?: string }
  endTime?: { hour: string; minute: string; ampm?: string }
  otherSubType?: string
  isBirthday?: boolean
  isBirthdayRequest?: boolean // Persistent flag: was ever marked as birthday
  originalBirthdayDate?: string | null // Which date is the birthday (MM/DD/YYYY)
  perDateHoursValues?: Record<string, HoursValues>
  perDateDurations?: Record<string, 'Full Day' | 'Partial Day'>
  perDateTimes?: Record<
    string,
    {
      from: { hour?: string; minute?: string; ampm?: string }
      to: { hour?: string; minute?: string; ampm?: string }
    }
  >
  perDateOtherSubTypeValues?: Record<string, string> // Per-date Other subtype selections
}

/**
 * Build time off details for selected dates
 * Supports both global and per-date hoursValues, durations, times, and other subtypes
 */
const buildTimeOffDetails = (
  params: BuildTimeOffDetailsParams
): TimeOffDetail[] => {
  const {
    selectedDates,
    hoursValues,
    duration,
    startTime,
    endTime,
    otherSubType,
    isBirthday,
    isBirthdayRequest,
    originalBirthdayDate,
    perDateHoursValues,
    perDateDurations,
    perDateTimes,
    perDateOtherSubTypeValues,
  } = params

  // Convert originalBirthdayDate from MM/DD/YYYY to YYYY-MM-DD for comparison
  const birthdayDateApiFormat = originalBirthdayDate 
    ? (() => {
        const [month, day, year] = originalBirthdayDate.split('/')
        return `${year}-${month}-${day}`
      })()
    : null

  // If no hours selected and not a birthday request, return empty
  if (
    !isBirthday &&
    !isBirthdayRequest &&
    !perDateHoursValues &&
    Object.values(hoursValues).every((h) => !h || Number(h) === 0)
  ) {
    return []
  }

  const details = selectedDates
    .map((dateStr) => {
      // Check if this date is the original birthday date
      const isThisDateBirthday = isBirthdayRequest && birthdayDateApiFormat && dateStr === birthdayDateApiFormat
      // Get hours for this specific date (use per-date if available, otherwise use global)
      const dateSpecificHours = perDateHoursValues?.[dateStr] || hoursValues

      // Resolve per-date Other subtype: check per-date storage first, then fall back to global
      const dateOtherSubType = perDateOtherSubTypeValues?.[dateStr] || otherSubType

      const timeOffAllocations = buildTimeOffAllocationDetails(
        dateSpecificHours,
        dateOtherSubType,
        isThisDateBirthday || isBirthday
      )

      // If no hours for this date and not a birthday request, return empty entry
      if (timeOffAllocations.length === 0 && !isBirthday && !isThisDateBirthday) {
        return null as any // This will be filtered out below
      }

      // Get duration for this date (use per-date if available, otherwise use global)
      const dateDuration = perDateDurations?.[dateStr] || duration
      const isFullDay = dateDuration === 'Full Day'
      const durationFlag: 'F' | 'P' = isFullDay ? 'F' : 'P'

      // Get times for this date (use per-date if available, otherwise use global)
      const dateStartTime = perDateTimes?.[dateStr]?.from || startTime
      const dateEndTime = perDateTimes?.[dateStr]?.to || endTime

      // Format start time (24-hour HH:MM:SS, derived from the 12-hour AM/PM fields)
      const formattedStartTime =
        !isFullDay && dateStartTime
          ? `${dateStr} ${formatPartialTimeToHHMMSS(dateStartTime.hour || '1', dateStartTime.minute || '00', dateStartTime.ampm)}`
          : null

      // Format end time (24-hour HH:MM:SS, derived from the 12-hour AM/PM fields)
      const formattedEndTime =
        !isFullDay && dateEndTime
          ? `${dateStr} ${formatPartialTimeToHHMMSS(dateEndTime.hour || '1', dateEndTime.minute || '00', dateEndTime.ampm)}`
          : null

      // For single day only, use 00:00:00 in day field. For multi-day, keep midnight
      const dayValue = `${dateStr} 00:00:00`

      const detail = {
        day: dayValue,
        durationFlag,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        timeOffAllocations,
      }

      return detail
    })
    .filter((detail) => detail !== null) // Remove null entries for dates with no hours

  return details
}

/**
 * Determine if request is for single day
 */
const determineSingleDay = (selectedDates: string[]): 'Y' | 'N' => {
  return selectedDates.length === 1 ? 'Y' : 'N'
}

/**
 * Parameters for building time-off request payload
 */
export interface PayloadBuilderParams {
  nuid: string // Logged-in user ID
  selectedDates: string[] // Array of YYYY-MM-DD format dates
  hoursValues: HoursValues // { vacation: '8', float: '0', ... }
  duration: 'Full Day' | 'Partial Day'
  startTime?: { hour: string; minute: string; ampm?: string } // For partial day
  endTime?: { hour: string; minute: string; ampm?: string } // For partial day
  comments: string
  status: 'SUBMITTED' | 'DRAFT' | 'RESUBMITTED'
  toReqId?: string // For draft-to-submit conversion (optional)
  otherSubType?: string // SubType code for 'other' category (e.g., 'OTH_ET')
  isBirthday?: boolean // Special flag for birthday requests (full day, no hours deduction)
  isBirthdayRequest?: boolean // Persistent flag: was ever marked as birthday (even after auto-uncheck)
  originalBirthdayDate?: string | null // Which date is the birthday (MM/DD/YYYY format)
  perDateHoursValues?: Record<string, HoursValues> // Per-date hours for multi-date requests with different hours per date
  perDateDurations?: Record<string, 'Full Day' | 'Partial Day'> // Per-date durations for multi-date requests
  perDateTimes?: Record<
    string,
    {
      from: { hour?: string; minute?: string; ampm?: string }
      to: { hour?: string; minute?: string; ampm?: string }
    }
  > // Per-date times for partial days
  perDateOtherSubTypeValues?: Record<string, string> // Per-date Other subtype selections (e.g., { "2026-11-25": "OTH_JD", "2026-11-26": "OTH_BL" })
  manualReqRcvdDate?: string
}

export const buildTimeOffRequestPayload = (
  params: PayloadBuilderParams
): CreateTimeOffRequestPayload => {
  const {
    nuid,
    selectedDates,
    hoursValues,
    duration,
    startTime,
    endTime,
    comments,
    status,
    toReqId = '',
    otherSubType,
    isBirthday,
    isBirthdayRequest,
    originalBirthdayDate,
    perDateHoursValues,
    perDateDurations,
    perDateTimes,
    perDateOtherSubTypeValues,
    manualReqRcvdDate
  } = params

  // Sort dates to get start and end
  const sortedDates = [...selectedDates].sort((a, b) => a.localeCompare(b))
  const startDate = sortedDates[0]
  const endDate = sortedDates.at(-1) || sortedDates[0]

  // Capture current time when user clicks Submit/Draft button
  const now = new Date()
  const currentTime = {
    hour: String(now.getHours()).padStart(2, '0'),
    minute: String(now.getMinutes()).padStart(2, '0'),
  }

  // Build time off details - pass per-date values if available
  const timeOffDetails = buildTimeOffDetails({
    selectedDates: sortedDates,
    hoursValues,
    duration,
    startTime,
    endTime,
    otherSubType,
    isBirthday,
    isBirthdayRequest,
    originalBirthdayDate,
    perDateHoursValues,
    perDateDurations,
    perDateTimes,
    perDateOtherSubTypeValues,
  })

  const payload = {
    nuid,
    startDate: `${startDate} ${formatTimeToHHMMSS(currentTime.hour, currentTime.minute)}`,
    endDate: `${endDate} ${formatTimeToHHMMSS(currentTime.hour, currentTime.minute)}`,
    isSingleDay: determineSingleDay(selectedDates),
    toReqId,
    status,
    comments,
    appCode: 'EMP_CURRENT',
    timeOffDetails,
     manualReqRcvdDate
  }

  return payload
}

/**
 * Validate payload before submission
 * Logs validation errors to console for debugging
 * Note: UI validation is already handled separately
 */
export const validatePayload = (
  payload: CreateTimeOffRequestPayload
): boolean => {
  const errors: string[] = []

  if (!payload.nuid) errors.push('User ID is required')
  if (!payload.startDate) errors.push('Start date is required')
  if (!payload.endDate) errors.push('End date is required')

  // Check if this is an education request (OTH_ET = Other - Education Time)
  const isEducationRequest = payload.timeOffDetails.some((detail) =>
    detail.timeOffAllocations?.some(
      (allocation) => allocation.toSubTypeCd === 'OTH_ET'
    )
  )

  // Comments are only required for education requests
  if (isEducationRequest && !payload.comments?.trim()) {
    errors.push('Comments are required for education requests')
  }

  // Allow empty timeOffDetails for birthday requests, otherwise require at least one detail
  if (payload.timeOffDetails.length === 0) {
    errors.push('No hours selected')
  }

  // Validate date format
  const dateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/
  if (!dateRegex.test(payload.startDate))
    errors.push('Invalid start date format')
  if (!dateRegex.test(payload.endDate)) errors.push('Invalid end date format')

  return errors.length === 0
}
